import React, { useEffect, useRef } from "react";
import { SectionComponent } from "../section";
import Icons from "../icons";
import { View } from "@components";
import "./style.scss";

type OwnProps = {
  title: string | React$Node,
  textNearEdit?: string,
  component: ComponentType<*>,
  rightInfo?: (row: Object) => React$Node,
  isReadonly?: boolean,
  hidden?: boolean,
  hideTitle?: boolean,
  nextButtonLabel?: string,
  // Received from a wizard
  data?: Object,
  editable?: boolean,
  onChange?: (values?: Object, valid?: boolean) => any,
  handleSubmit?: (values: Object, valid: boolean) => any,
  isTouched?: boolean,
  isValid?: boolean,
  isCollapsable?: boolean,
  onToggleEdit?: () => any,
  getStepState?: (step: number) => any,
  step?: number,
  currentStep?: number,
};

const getStepColor = (editable, isTouched, isValid) => {
  if (editable) {
    return "black";
  } else if (isTouched && !isValid) {
    return "negative";
  } else if (!isTouched) {
    return "disabled";
  }
  return "green";
};

function Step(props) {
  const currentStepRef = useRef(null);

  const {
    component: Handler,
    title,
    hideTitle,
    onChange,
    handleSubmit,
    editable,
    isReadonly,
    data = {},
    isTouched,
    key,
    isValid,
    isCollapsable,
    onToggleEdit,
    rightInfo,
    textNearEdit,
    ...rest
  } = props;
  const currentStep = !!currentStep ? currentStep : 0;
  const step = props.step || 0;
  let focusableElements;
  useEffect(() => {
    const stepEl = document.querySelector(".step.editable");
    if (currentStepRef?.current?.props?.editable && stepEl) {
      focusableElements = stepEl.querySelectorAll(
        "input:not(:disabled):not(:read-only), button:not(:disabled), textarea:not(:disabled):not(:read-only)",
      );
      addTabIndex(focusableElements);
      stepEl.addEventListener("keydown", onKeyDown);
    }

    return function() {
      if (currentStepRef?.current?.props?.editable && stepEl) {
        stepEl.removeEventListener("keydown", onKeyDown);
      }
    };
  }, [data]);

  function addTabIndex(elements: HTMLElement[]) {
    elements.forEach((el: HTMLElement, idx: number) => {
      el.setAttribute("tabindex", 0);
    });
  }

  function onKeyDown(e: KeyboardEvent) {
    if (!e.ctrlKey || e.key !== "Enter") return;
    const submitButton = document.querySelector("#submitButton");
    if (submitButton) submitButton.click();
  }

  const handler = (
    <Handler
      ref={currentStepRef}
      onChange={onChange}
      handleSubmit={handleSubmit}
      editable={editable}
      data={data}
      isTouched={isTouched}
      isValid={isValid}
      isCollapsable={isCollapsable}
      onToggleEdit={onToggleEdit}
      {...rest}
    />
  );

  if (hideTitle) {
    return handler;
  }

  return (
    <View
      className={["step", editable && "editable", isTouched && "is-touched", isValid && "is-valid"]}
    >
      <SectionComponent
        title={
          <div className="d-flex align-items-center">
            {!editable && isTouched && isValid ? (
              <Icons.StepCheckIcon color="positive" />
            ) : (
              <Icons.StepCircleIcon color={getStepColor(editable, isTouched, isValid)} />
            )}
            <h3>{title}</h3>
          </div>
        }
        collapsable={isCollapsable}
        compact
        rightLink={
          !isCollapsable &&
          isTouched &&
          !editable && (
            <div>
              {rightInfo && <span>{rightInfo(data)}</span>}
              {textNearEdit && (
                <span className="text-orange small-font-size font-weight-normal mr-2">
                  {textNearEdit}
                </span>
              )}
              {(!step || !currentStep || currentStep >= step) && isValid && !isReadonly && (
                <Icons.PencilIcon onClick={onToggleEdit} />
              )}
            </div>
          )
        }
        active={!isCollapsable && editable}
        disabled={!editable && !isTouched}
        isOpen={isCollapsable ? editable : true}
        onTitleClick={isCollapsable ? onToggleEdit : undefined}
        defaultBorder
      >
        {handler}
      </SectionComponent>
    </View>
  );
}

export default Step;
